import type { PnlCardData } from '@/features/trading/utils/pnlCardData';
import { priceAmount, tokenAmount } from '@/features/trading/utils/tradeFormat';
import { type PublicTapeTrade, USDC_DECIMALS } from '@/lib/api/speed';

import { markForTrade, roiPercent, unrealizedPnl } from './unrealized';

/**
 * The share card for a tape row, built from the row alone.
 *
 * The terminal's own cards read the viewer's position from the backend; a tape
 * row is someone else's trade, so there is nothing to read — the row already
 * carries entry, qty, margin and (once closed) the realized pnl and exit.
 *
 * An open row is priced against the current mark, the same figure its result
 * cell shows, and the card is a snapshot of that moment. Null exactly when the
 * result cell shows a dash: no side, or no figure to put on the card.
 */
export function pnlCardFromTapeTrade(
    trade: PublicTapeTrade,
    marks: Map<string, number>,
): PnlCardData | null {
    if (trade.isLong === null) return null;

    const isOpen = trade.status === 'Open';
    const mark = isOpen ? markForTrade(trade, marks) : null;
    const pnlValue = isOpen
        ? unrealizedPnl(trade, mark)
        : priceAmount(trade.pnl);
    if (pnlValue === null) return null;

    const entryPrice = priceAmount(trade.entryPx);
    const sizeToken = priceAmount(trade.qty);
    return {
        kind: isOpen ? 'unrealized' : 'realized',
        coin: (trade.coin ?? '—').toUpperCase(),
        side: trade.isLong ? 'LONG' : 'SHORT',
        leverage: trade.leverage,
        pnlValue,
        pnlPercent: roiPercent(pnlValue, trade.margin),
        invested: tokenAmount(trade.margin, USDC_DECIMALS),
        positionValue:
            sizeToken !== null && entryPrice !== null
                ? sizeToken * entryPrice
                : null,
        entryPrice,
        markPrice: isOpen ? mark : priceAmount(trade.closePx),
        sizeToken,
    };
}
